import { Injectable } from "@angular/core";
import { Subject, Subscription } from "rxjs";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { Router } from "@angular/router";
import { BfxSaveProfileNotificationService } from "app/bfx-common/services/bfx-save-profile-notification.service";
import { BfxScratchPadService } from "../../bfx-blotter-scratchpad/services/bfx-scratchpad.service";

/**
 * BfxFooterBarMessageEvent
 * ----------------------------------
 * This model is used for dissemination of commands from the footerBarHandler service
 * to the footerBar component which displays the message .
 */
export class BfxFooterBarMessageEvent {
  // must
  public message: string;
  public duration: number;

  constructor(_message: string, _duration: number) {
    this.message = _message;
    this.duration = _duration < 1000 ? _duration * 1000 : _duration;
  }
}

@Injectable()
export class BfxFooterBarHandlerService {
  /**********************************************************************/
  /* DATA */
  // config
  private defaultMessageDuration: number = 10 * 1000; // in sec
  // observables
  private routerSubscription: Subscription;
  private userProfileNotificationSubscription: Subscription;
  private scratchPadNotificationSubscription: Subscription;
  // message
  private messageEventSource = new Subject<any>();
  private messageCtrl$ = this.messageEventSource.asObservable();

  /**********************************************************************/
  /* CONSTRUCTOR  */

  constructor(
    private _router: Router,
    private _saveProfileNotificationService: BfxSaveProfileNotificationService,
    private _scratchPadService: BfxScratchPadService,
  ) {
    this.initialize();
  }
  /**********************************************************************/
  /* INITIALIZATION */
  private initialize() {
    // Subscription init
    this.userProfileNotificationSubscription =
      this._saveProfileNotificationService.footerCtrl$.subscribe(this.onEvent.bind(this));
    this.scratchPadNotificationSubscription = this._scratchPadService.footerCtrl$.subscribe(
      this.onEvent.bind(this),
    );

    // Router handler
    this.routerSubscription = this._router.events.subscribe((url: any) => {
      if (this._router.url == "/logout") {
        this.routerSubscription.unsubscribe();
        if (this.userProfileNotificationSubscription != null)
          this.userProfileNotificationSubscription.unsubscribe();
        if (this.scratchPadNotificationSubscription != null)
          this.scratchPadNotificationSubscription.unsubscribe();
      }
    });
  }

  /**********************************************************************/
  /* EVENT HANDLERS */
  private onEvent($event: any): void {
    this.dispatchMessage(this.processEvent($event));
  }

  /**********************************************************************/
  /* PUBLIC API */

  public getMessageCtrlEventSubject(): any {
    return this.messageCtrl$;
  }

  /**********************************************************************/
  /* UTILITY */
  private processEvent($event): BfxFooterBarMessageEvent {
    if ($event != null) {
      let message: string = null;
      let duration: number = null;
      if ($event instanceof BfxFooterBarMessageEvent) {
        return $event;
      } else if (typeof $event === "string") {
        message = $event;
        duration = this.defaultMessageDuration;
      } else if ($event instanceof Object) {
        if ((<Object>$event).hasOwnProperty("message")) {
          message = $event.message;
        } else if ((<Object>$event).hasOwnProperty("msg")) {
          message = $event.msg;
        }

        if ((<Object>$event).hasOwnProperty("duration")) {
          duration = $event.duration;
        } else {
          duration = this.defaultMessageDuration;
        }
      }

      if (typeof message === "string" && message.length > 0 && typeof duration) {
        return new BfxFooterBarMessageEvent(message, duration);
      } else {
        logger.out.error(
          `ERROR: FooterBarHandler.processEvent(): Invalid params => message: ${message} | duration: ${duration}`,
        );
        return null;
      }
    } else {
      logger.out.error(`ERROR: FooterBarHandler.processEvent(): Event is null!`);
      return null;
    }
  }

  private dispatchMessage(messageEvent: BfxFooterBarMessageEvent) {
    if (messageEvent instanceof BfxFooterBarMessageEvent) {
      this.messageEventSource.next(messageEvent);
    }
  }
}
